import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserPlus, ShieldCheck, User2, ArrowLeft, Ban, Undo2, FileDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { generateNurseManualPdf } from "@/lib/manual-pdf";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [toggleTarget, setToggleTarget] = useState(null); // user obj pending confirm
  const [toggling, setToggling] = useState(false);
  const [downloadingManual, setDownloadingManual] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error cargando usuarios");
      setUsers([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("La contrasena debe tener al menos 6 caracteres");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Enfermera creada");
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear");
    } finally {
      setSaving(false);
    }
  };

  const downloadManual = async () => {
    if (downloadingManual) return;
    setDownloadingManual(true);
    try {
      const fname = await generateNurseManualPdf();
      toast.success(`Manual descargado: ${fname}`);
    } catch (err) {
      console.error(err);
      toast.error("No se pudo generar el manual");
    } finally {
      setDownloadingManual(false);
    }
  };

  const doToggle = async () => {
    if (!toggleTarget) return;
    setToggling(true);
    try {
      const { data } = await api.post(`/admin/users/${toggleTarget.id}/toggle-active`);
      toast.success(
        data.is_active
          ? `${data.name} fue reactivada`
          : `${data.name} fue suspendida. Su acceso queda bloqueado.`
      );
      setToggleTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cambiar el estado");
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AppHeader subtitle="Administracion" />

      <main className="mx-auto max-w-3xl px-4 py-6">
        <Button
          variant="ghost"
          className="mb-3 h-10 px-2 text-slate-600"
          onClick={() => nav("/dashboard")}
          data-testid="admin-back-btn"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Volver
        </Button>

        <div className="mb-4 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-4 shadow-sm" data-testid="admin-manual-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#1D4ED8] text-white">
                <FileDown className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">Manual para enfermeras</p>
                <p className="mt-0.5 text-xs text-slate-600">
                  Guia PDF de 8 paginas (incluye DPCA y Gastrostomia PEG) para compartir con nuevas enfermeras junto con sus credenciales.
                </p>
              </div>
            </div>
            <Button
              onClick={downloadManual}
              disabled={downloadingManual}
              className="h-10 rounded-xl bg-[#1D4ED8] px-4 text-sm font-semibold hover:bg-[#1E40AF]"
              data-testid="admin-download-manual-btn"
            >
              {downloadingManual ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="mr-1.5 h-4 w-4" />
              )}
              Descargar manual PDF
            </Button>
          </div>
        </div>

        <div className="mb-5 flex items-center justify-between">
          <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
            Enfermeras registradas
          </h1>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-11 rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF]" data-testid="admin-add-nurse-btn">
                <UserPlus className="mr-2 h-4 w-4" /> Crear enfermera
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear cuenta de enfermera</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label className="mb-1 block text-sm font-semibold">Nombre completo</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="h-11"
                    data-testid="admin-new-nurse-name"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-sm font-semibold">Correo</Label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="h-11"
                    data-testid="admin-new-nurse-email"
                  />
                </div>
                <div>
                  <Label className="mb-1 block text-sm font-semibold">Contrasena</Label>
                  <Input
                    type="password"
                    required
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="h-11"
                    data-testid="admin-new-nurse-password"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-[#1D4ED8] hover:bg-[#1E40AF]"
                    data-testid="admin-save-nurse-btn"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {users === null && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        )}

        <div className="space-y-2">
          {users?.map((u) => {
            const isSuspended = u.is_active === false;
            const isAdmin = u.role === "admin";
            const isSelf = currentUser?.id === u.id;
            return (
              <div
                key={u.id}
                className={`flex items-center justify-between gap-3 rounded-xl border p-4 shadow-sm ${
                  isSuspended ? "border-red-200 bg-red-50/40" : "border-slate-200 bg-white"
                }`}
                data-testid={`admin-user-${u.id}`}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                      isAdmin
                        ? "bg-amber-100 text-amber-700"
                        : isSuspended
                        ? "bg-slate-200 text-slate-500"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {isAdmin ? <ShieldCheck className="h-5 w-5" /> : <User2 className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0">
                    <div className={`truncate font-semibold ${isSuspended ? "text-slate-500 line-through" : "text-slate-900"}`}>
                      {u.name}
                    </div>
                    <div className="truncate text-xs text-slate-500">{u.email}</div>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                      isAdmin
                        ? "bg-amber-100 text-amber-800"
                        : isSuspended
                        ? "bg-red-100 text-red-800"
                        : "bg-green-100 text-green-800"
                    }`}
                    data-testid={`admin-user-status-${u.id}`}
                  >
                    {isAdmin ? "Admin" : isSuspended ? "Suspendida" : "Activa"}
                  </span>
                  {!isAdmin && !isSelf && (
                    <Button
                      size="sm"
                      variant={isSuspended ? "outline" : "ghost"}
                      className={`h-9 rounded-lg px-2 ${
                        isSuspended
                          ? "border-green-300 text-green-700 hover:bg-green-50"
                          : "text-red-600 hover:bg-red-50"
                      }`}
                      onClick={() => setToggleTarget(u)}
                      data-testid={`admin-user-toggle-${u.id}`}
                      title={isSuspended ? "Reactivar acceso" : "Suspender acceso"}
                    >
                      {isSuspended ? (
                        <>
                          <Undo2 className="mr-1 h-4 w-4" /> Reactivar
                        </>
                      ) : (
                        <>
                          <Ban className="mr-1 h-4 w-4" /> Suspender
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <AlertDialog open={!!toggleTarget} onOpenChange={(o) => !o && !toggling && setToggleTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {toggleTarget?.is_active === false ? "Reactivar acceso?" : "Suspender acceso?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {toggleTarget?.is_active === false ? (
                <>
                  <strong>{toggleTarget?.name}</strong> volvera a poder iniciar sesion y crear programas de cuidados.
                </>
              ) : (
                <>
                  <strong>{toggleTarget?.name}</strong> ya no podra iniciar sesion ni crear nuevos programas.
                  Su historial y programas anteriores se conservan intactos.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggling} data-testid="admin-toggle-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                toggleTarget?.is_active === false
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }
              onClick={doToggle}
              disabled={toggling}
              data-testid="admin-toggle-confirm"
            >
              {toggling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : toggleTarget?.is_active === false ? (
                "Si, reactivar"
              ) : (
                "Si, suspender"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
